import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Public pages
import HomePage from './pages/public/HomePage';
import PricingPage from './pages/public/PricingPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import LoginPage from './pages/public/LoginPage';
import RegisterPage from './pages/public/RegisterPage';

// App pages
import DashboardPage from './pages/app/DashboardPage';
import ScannerPage from './pages/app/ScannerPage';
import InventoryPage from './pages/app/InventoryPage';
import InventoryDetailPage from './pages/app/InventoryDetailPage';
import ListsPage from './pages/app/ListsPage';
import ExportPage from './pages/app/ExportPage';
import ConsignorsPage from './pages/app/ConsignorsPage';
import ProfilePage from './pages/app/ProfilePage';
import SettingsPage from './pages/app/SettingsPage';

// Admin pages
import AdminDashboardPage from './pages/app/admin/AdminDashboardPage';
import UsersPage from './pages/app/admin/UsersPage';
import UserInvitePage from './pages/app/admin/UserInvitePage';
import CompanySettingsPage from './pages/app/admin/CompanySettingsPage';

// Super Admin pages
import SuperAdminPage from './pages/app/superadmin/SuperAdminPage';
import CompaniesPage from './pages/app/superadmin/CompaniesPage';

// Layouts
import PublicLayout from './components/layouts/PublicLayout';
import AppLayout from './components/layouts/AppLayout';
import AdminLayout from './components/layouts/AdminLayout';
import SuperAdminLayout from './components/layouts/SuperAdminLayout';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
      </Route>
      
      {/* App routes (require authentication) */}
      <Route path="/app" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="scanner" element={<ScannerPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/:id" element={<InventoryDetailPage />} />
        <Route path="lists" element={<ListsPage />} />
        <Route path="export" element={<ExportPage />} />
        <Route path="consignors" element={<ConsignorsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      
      {/* Admin routes (require COMPANY_ADMIN role) */}
      <Route path="/app/admin" element={
        <ProtectedRoute allowedRoles={['COMPANY_ADMIN', 'SUPER_ADMIN']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/invite" element={<UserInvitePage />} />
        <Route path="settings" element={<CompanySettingsPage />} />
      </Route>
      
      {/* Super Admin routes (require SUPER_ADMIN role) */}
      <Route path="/app/superadmin" element={
        <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
          <SuperAdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<SuperAdminPage />} />
        <Route path="companies" element={<CompaniesPage />} />
      </Route>
      
      {/* Redirect to home for any other routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
